import React from "react";
import { darken } from "./colorUtils";

const STAR_PATH = "M50,5 L61,39 L96,39 L68,60 L79,94 L50,73 L21,94 L32,60 L4,39 L39,39 Z";

const STARS = [
  { cx: 12, cy: 14, scale: 0.08, color: "#FFFFFF" },
  { cx: 82, cy: 12, scale: 0.10, color: "#F0BE39" },
  { cx: 30, cy: 28, scale: 0.05, color: "#FFFFFF" },
  { cx: 66, cy: 34, scale: 0.09, color: "#FFFFFF" },
  { cx: 46, cy: 20, scale: 0.06, color: "#FFFFFF" },
  { cx: 18, cy: 52, scale: 0.08, color: "#F0BE39" },
  { cx: 88, cy: 58, scale: 0.06, color: "#FFFFFF" },
  { cx: 42, cy: 66, scale: 0.10, color: "#FFFFFF" },
  { cx: 72, cy: 78, scale: 0.07, color: "#F0BE39" },
  { cx: 22, cy: 88, scale: 0.08, color: "#FFFFFF" },
  { cx: 58, cy: 90, scale: 0.05, color: "#FFFFFF" },
  { cx: 94, cy: 86, scale: 0.06, color: "#FFFFFF" },
];

const METEORS = [
  { startX: 88, startY:  8, size: 1.0, delay:  0,  dur: 12 },
  { startX: 96, startY: 22, size: 0.8, delay: -2,  dur: 12 },
  { startX: 76, startY:  4, size: 1.1, delay: -4,  dur: 13 },
  { startX: 82, startY: 18, size: 0.9, delay: -6,  dur: 11 },
  { startX: 92, startY: 30, size: 0.7, delay: -8,  dur: 12 },
  { startX: 86, startY: 38, size: 1.0, delay: -10, dur: 13 },
];

const DOTS = [
  { cx:  4, cy:  8,  r: 0.4 },  { cx: 24, cy:  6,  r: 0.3 },  { cx: 38, cy:  9,  r: 0.35 },
  { cx: 54, cy:  4,  r: 0.3 },  { cx: 72, cy:  8,  r: 0.4 },  { cx: 94, cy:  6,  r: 0.3 },
  { cx:  8, cy: 22,  r: 0.3 },  { cx: 20, cy: 20,  r: 0.35 }, { cx: 38, cy: 22,  r: 0.25 },
  { cx: 52, cy: 30,  r: 0.3 },  { cx: 62, cy: 24,  r: 0.3 },  { cx: 78, cy: 22,  r: 0.35 },
  { cx: 92, cy: 28,  r: 0.3 },  { cx:  4, cy: 36,  r: 0.35 }, { cx: 16, cy: 40,  r: 0.3 },
  { cx: 36, cy: 40,  r: 0.3 },  { cx: 54, cy: 42,  r: 0.35 }, { cx: 68, cy: 46,  r: 0.25 },
  { cx: 82, cy: 40,  r: 0.3 },  { cx: 96, cy: 44,  r: 0.35 }, { cx:  6, cy: 56,  r: 0.3 },
  { cx: 26, cy: 58,  r: 0.35 }, { cx: 40, cy: 56,  r: 0.25 }, { cx: 58, cy: 58,  r: 0.3 },
  { cx: 76, cy: 56,  r: 0.3 },  { cx: 94, cy: 68,  r: 0.35 }, { cx: 12, cy: 72,  r: 0.3 },
  { cx: 30, cy: 74,  r: 0.25 }, { cx: 50, cy: 74,  r: 0.35 }, { cx: 64, cy: 72,  r: 0.3 },
  { cx: 82, cy: 70,  r: 0.3 },  { cx:  2, cy: 86,  r: 0.35 }, { cx: 34, cy: 84,  r: 0.3 },
  { cx: 48, cy: 86,  r: 0.25 }, { cx: 66, cy: 86,  r: 0.3 },  { cx: 78, cy: 92,  r: 0.35 },
  { cx: 10, cy: 96,  r: 0.3 },  { cx: 40, cy: 96,  r: 0.25 }, { cx: 90, cy: 96,  r: 0.3 },
];

export const StarsBackground = ({ bgColor = "#3E8DFF" }) => {
  const topColor = darken(bgColor, 0.55);
  const midColor = darken(bgColor, 0.25);
  return (
    <div className="absolute w-full h-full overflow-hidden">
      <svg
        preserveAspectRatio="xMidYMid slice"
        className="w-full h-full"
        viewBox="0 0 100 100"
      >
        <defs>
          <linearGradient id="starBg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor={topColor} />
            <stop offset="55%" stopColor={midColor} />
            <stop offset="100%" stopColor={bgColor} />
          </linearGradient>
          <linearGradient id="meteorTrail" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="22" y2="0">
            <stop offset="0%"   stopColor="#FFFFFF" stopOpacity="1" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
          </linearGradient>
          <filter id="starGlow" x="-200%" y="-200%" width="500%" height="500%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="wide" />
            <feGaussianBlur in="SourceGraphic" stdDeviation="0.8" result="tight" />
            <feMerge>
              <feMergeNode in="wide" />
              <feMergeNode in="wide" />
              <feMergeNode in="tight" />
              <feMergeNode in="tight" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="dotGlow" x="-200%" y="-200%" width="500%" height="500%">
            <feGaussianBlur stdDeviation="0.45" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <style>{`
            @keyframes starTwinkle {
              0%,100% { opacity: 0.35; transform: scale(0.85); }
              50%     { opacity: 1;    transform: scale(1.15); }
            }
            @keyframes meteorSweep {
              0%,82%  { transform: translate(0px, 0px);     opacity: 0; }
              85%     { transform: translate(0px, 0px);     opacity: 1; }
              98%     { transform: translate(-110px, 64px); opacity: 1; }
              100%    { transform: translate(-110px, 64px); opacity: 0; }
            }
            .twinkle { animation: starTwinkle ease-in-out infinite; transform-origin: center; transform-box: fill-box; }
            .meteor  { animation-name: meteorSweep; animation-timing-function: linear; animation-iteration-count: infinite; }
          `}</style>
        </defs>

        <rect x="0" y="0" width="100" height="100" fill="url(#starBg)" />

        {DOTS.map((d, i) => (
          <circle
            key={`d-${i}`}
            className="twinkle"
            cx={d.cx}
            cy={d.cy}
            r={d.r}
            fill="#ffffff"
            filter="url(#dotGlow)"
            style={{
              animationDelay: `${-(i * 0.9) % 10}s`,
              animationDuration: `${6 + (i % 7)}s`,
            }}
          />
        ))}

        {STARS.map((s, i) => (
          <path
            key={`s-${i}`}
            d={STAR_PATH}
            fill={s.color}
            opacity="0.95"
            filter="url(#starGlow)"
            transform={`translate(${s.cx - 50 * s.scale}, ${s.cy - 50 * s.scale}) scale(${s.scale})`}
          />
        ))}

        {METEORS.map((m, i) => (
          <g key={`m-${i}`} transform={`translate(${m.startX} ${m.startY})`}>
            <g
              className="meteor"
              style={{ animationDelay: `${m.delay}s`, animationDuration: `${m.dur}s` }}
            >
              <g transform={`rotate(-31) scale(${m.size})`}>
                <polygon points="0,-0.8 0,0.8 22,0" fill="url(#meteorTrail)" />
                <circle cx="0" cy="0" r="1" fill="#ffffff" filter="url(#dotGlow)" />
              </g>
            </g>
          </g>
        ))}
      </svg>
    </div>
  );
};
